/**
 * Error telemetry (FRONTEND_PLAN F18): console only — no third party, no network, no PII.
 *
 * One structured line per failure carries what the runbook needs (docs/runbook.md): the build,
 * the route, and the backend's `request_id` when the failure was an API error. Message bodies and
 * customer data are never included; the ApiError's `message` is the backend's own copy.
 */
import { ApiError } from '@/api/errors'
import { env } from '@/config/env'

export interface ErrorReport {
  readonly at: string
  readonly build: string
  readonly env: string
  readonly route: string
  readonly source: 'route-boundary' | 'window.error' | 'unhandledrejection' | 'manual'
  readonly name: string
  readonly message: string
  readonly status?: number
  readonly code?: string
  readonly subCode?: string
  readonly requestId?: string
}

export function describeForTelemetry(
  error: unknown,
  source: ErrorReport['source'],
  route: string = globalThis.location?.pathname ?? '',
): ErrorReport {
  const base = {
    at: new Date().toISOString(),
    build: env.buildSha,
    env: env.appEnv,
    route,
    source,
  }
  if (error instanceof ApiError) {
    return {
      ...base,
      name: 'ApiError',
      message: error.message,
      status: error.status,
      code: error.code,
      ...(error.subCode === undefined ? {} : { subCode: error.subCode }),
      ...(error.requestId === undefined ? {} : { requestId: error.requestId }),
    }
  }
  if (error instanceof Error) return { ...base, name: error.name, message: error.message }
  return { ...base, name: 'UnknownError', message: String(error) }
}

/** The single sink. Swap the body for a real transport later; callers never change. */
export function reportError(error: unknown, source: ErrorReport['source'] = 'manual'): ErrorReport {
  const report = describeForTelemetry(error, source)
  console.error('[recoup:error]', report)
  return report
}

let installed = false

/** Window-level hooks for anything that escapes React and the router boundary. Idempotent. */
export function installErrorTelemetry(target: Window = window): void {
  if (installed) return
  installed = true
  target.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, 'window.error')
  })
  target.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, 'unhandledrejection')
  })
}
